import React, { useEffect, useState } from "react";
import {
  Loader2, Send, Save, Plus, Trash2, MessageCircle, History, ShieldCheck, PackageCheck,
} from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";
import { errMsg } from "./hqUtils";

/* HQ — Priority Order. A shared workspace with Heritage: check the order,
   fix anything wrong, message us, then confirm it's complete and correct. */

const EDGE = "#EFE4CC";
const PLUM = "var(--hq-accent, #6F4A7A)";
const DEEP = "var(--hq-deep, #5A4565)";
const when = (iso) => iso ? new Date(iso).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function HQPriorityOrder() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newOrder, setNewOrder] = useState({ order_number: "", notes: "", items: [{ name: "", qty: 1 }] });

  const load = async () => {
    try {
      const r = await customerApi.get("/customer/priority");
      setData(r.data);
    } catch (e) { toast.error(errMsg(e, "Couldn't load your priority order.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendMessage = async () => {
    if (!draft.trim()) return;
    setBusy("message");
    try {
      await customerApi.post("/customer/priority/messages", { body: draft });
      setDraft("");
      await load();
    } catch (e) { toast.error(errMsg(e, "Couldn't send that message.")); }
    finally { setBusy(""); }
  };

  const confirmAll = async () => {
    setBusy("confirm");
    try {
      await customerApi.post("/customer/priority/confirm");
      toast.success("Thank you — your order is confirmed. Heritage will take it from here.");
      setConfirming(false);
      await load();
    } catch (e) { toast.error(errMsg(e, "Couldn't confirm your order.")); }
    finally { setBusy(""); }
  };

  const addOrder = async () => {
    setBusy("add");
    try {
      await customerApi.post("/customer/priority/orders", {
        order_number: newOrder.order_number,
        notes: newOrder.notes,
        line_items: newOrder.items.filter((i) => i.name.trim()).map((i) => ({ name: i.name, qty: Number(i.qty) || 1 })),
      });
      toast.success("Thanks — we've added that order for Heritage to check.");
      setAdding(false);
      setNewOrder({ order_number: "", notes: "", items: [{ name: "", qty: 1 }] });
      await load();
    } catch (e) { toast.error(errMsg(e, "Couldn't add that order.")); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return (
    <div className="bg-white border border-dashed p-8 text-center" style={{ borderColor: EDGE }} data-testid="hq-priority-none">
      <div className="text-xl">Your order isn't in the Priority Queue.</div>
      <p className="text-sm italic opacity-70 mt-1">If you think it should be, message Heritage and we'll take a look.</p>
    </div>
  );

  const locked = !!data.confirmed_at;

  return (
    <div className="space-y-6" data-testid="hq-priority-order" style={{ color: DEEP }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Priority queue</div>
        <h1 className="text-4xl mt-1.5">Priority Order</h1>
        <p className="italic opacity-75 text-sm mt-1 max-w-2xl">
          This is your order exactly as we have it. Please check the varieties, quantities, payments and shipping.
          Fix anything that's wrong, message us with questions, then confirm when it's right.
        </p>
        <div className="mt-2 text-xs">
          Status: <strong data-testid="hq-priority-status">{data.priority_status}</strong>
          {data.confirmed_at && <span data-testid="hq-priority-confirmed"> · confirmed {when(data.confirmed_at)}</span>}
        </div>
      </header>

      {locked ? (
        <div className="bg-[#EAF3EC] border-l-4 border-[#1F5B3F] p-4 text-sm text-[#1F5B3F]" data-testid="hq-priority-locked">
          <ShieldCheck className="w-4 h-4 inline mr-1.5 mb-0.5" />
          You confirmed this order on {when(data.confirmed_at)}. It's locked while Heritage prepares it — message us if something needs changing.
        </div>
      ) : (
        <div className="bg-[#F2EAF3] border-l-4 border-[#6E1E3A] p-4 space-y-3" data-testid="hq-priority-confirm-bar">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <strong>Everything look right?</strong> Confirm and we'll move your order into packing.
            </div>
            <button type="button" onClick={() => setConfirming(true)} disabled={busy === "confirm"} data-testid="hq-priority-confirm"
              className="h-12 px-6 bg-[#6E1E3A] text-white text-xs font-semibold tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-40">
              <PackageCheck className="w-4 h-4" /> My order is complete and correct
            </button>
          </div>
          {confirming && (
            <div className="bg-white border p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: EDGE }} data-testid="hq-priority-confirm-dialog">
              <p className="text-sm max-w-lg">
                We'll lock this version of your order and move it towards packing. You can still message us if something changes.
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setConfirming(false)} data-testid="hq-priority-confirm-cancel"
                  className="h-11 px-4 border text-sm" style={{ borderColor: EDGE }}>Not yet</button>
                <button type="button" onClick={confirmAll} disabled={busy === "confirm"} data-testid="hq-priority-confirm-yes"
                  className="h-11 px-5 bg-[#1F5B3F] text-white text-xs font-semibold tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-40">
                  {busy === "confirm" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Yes, confirm my order
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        {[["Orders", data.summary.order_count], ["Total", `NZ$${Number(data.summary.total_value).toFixed(2)}`],
          ["Paid", `NZ$${Number(data.summary.paid_value).toFixed(2)}`], ["Owing", `NZ$${Number(data.summary.owing_value).toFixed(2)}`]].map(([l, v]) => (
          <div key={l} className="bg-white border p-3" style={{ borderColor: EDGE }}>
            <div className="text-[10px] tracking-[0.25em] uppercase opacity-60">{l}</div>
            <div className="font-serif text-2xl mt-0.5">{v}</div>
          </div>
        ))}
      </div>

      {data.orders.length > 1 && (
        <p className="text-sm italic" data-testid="hq-priority-together">
          These {data.orders.length} orders are being prepared and shipped together.
        </p>
      )}

      {data.orders.map((o) => <HQOrderCard key={o.id} order={o} locked={locked} onSaved={load} />)}

      {!locked && (
        <section className="bg-white border p-4" style={{ borderColor: EDGE }} data-testid="hq-priority-add-order">
          {!adding ? (
            <button type="button" onClick={() => setAdding(true)} data-testid="hq-priority-add-open"
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.14em] uppercase" style={{ color: PLUM }}>
              <Plus className="w-4 h-4" /> An order of mine is missing
            </button>
          ) : (
            <div className="space-y-3">
              <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: PLUM }}>Add a missing order</div>
              <input value={newOrder.order_number} onChange={(e) => setNewOrder({ ...newOrder, order_number: e.target.value })}
                placeholder="Order number (if you have it)" data-testid="hq-priority-new-number"
                className="w-full h-11 px-3 border bg-white text-base" style={{ borderColor: EDGE }} />
              {newOrder.items.map((i, idx) => (
                <div key={idx} className="flex gap-2">
                  <input value={i.name} onChange={(e) => setNewOrder({ ...newOrder, items: newOrder.items.map((x, n) => n === idx ? { ...x, name: e.target.value } : x) })}
                    placeholder="Variety" data-testid={`hq-priority-new-item-${idx}`}
                    className="flex-1 h-11 px-3 border bg-white text-base" style={{ borderColor: EDGE }} />
                  <input type="number" min="1" value={i.qty} onChange={(e) => setNewOrder({ ...newOrder, items: newOrder.items.map((x, n) => n === idx ? { ...x, qty: e.target.value } : x) })}
                    className="w-20 h-11 px-3 border bg-white text-base text-center" style={{ borderColor: EDGE }} />
                </div>
              ))}
              <button type="button" onClick={() => setNewOrder({ ...newOrder, items: [...newOrder.items, { name: "", qty: 1 }] })}
                className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: PLUM }}>+ Another variety</button>
              <textarea value={newOrder.notes} onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })} rows={2}
                placeholder="Anything that helps us find it (date ordered, payment made, etc.)" data-testid="hq-priority-new-notes"
                className="w-full p-3 border bg-white text-base" style={{ borderColor: EDGE }} />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setAdding(false)} className="h-11 px-4 border text-sm" style={{ borderColor: EDGE }}>Cancel</button>
                <button type="button" onClick={addOrder} disabled={busy === "add"} data-testid="hq-priority-new-save"
                  className="h-11 px-5 bg-[#6E1E3A] text-white text-xs font-semibold tracking-[0.14em] uppercase disabled:opacity-40">Send to Heritage</button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="bg-white border" style={{ borderColor: EDGE }} data-testid="hq-priority-thread">
        <div className="px-4 py-3 border-b text-[10px] tracking-[0.3em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ borderColor: EDGE, color: PLUM }}>
          <MessageCircle className="w-4 h-4" /> Messages with Heritage
        </div>
        <div className="p-3 space-y-2 max-h-[420px] overflow-y-auto">
          {data.messages.length === 0 && <p className="text-sm italic opacity-60 px-1">No messages yet — ask us anything.</p>}
          {data.messages.map((m) => (
            <div key={m.id} data-testid={`hq-priority-message-${m.id}`}
              className={`p-3 rounded text-sm ${m.sender === "customer" ? "bg-[#F2EAF3] ml-6" : "bg-[#FBF6E9] mr-6"}`}>
              <div className="text-[10px] tracking-[0.15em] uppercase font-bold opacity-60 flex justify-between gap-2">
                <span>{m.sender === "customer" ? "You" : "Heritage"}</span>
                <span>{when(m.created_at)}</span>
              </div>
              <div className="mt-1 whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t space-y-2" style={{ borderColor: EDGE }}>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder="Message Heritage…"
            data-testid="hq-priority-message-input" className="w-full p-3 border bg-white text-base" style={{ borderColor: EDGE }} />
          <button type="button" onClick={sendMessage} disabled={busy === "message" || !draft.trim()} data-testid="hq-priority-message-send"
            className="w-full h-12 bg-[#6E1E3A] text-white text-xs font-semibold tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-40">
            {busy === "message" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send message
          </button>
        </div>
      </section>

      <section className="bg-white border" style={{ borderColor: EDGE }} data-testid="hq-priority-history">
        <div className="px-4 py-3 border-b text-[10px] tracking-[0.3em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ borderColor: EDGE, color: PLUM }}>
          <History className="w-4 h-4" /> Change history
        </div>
        <ul className="divide-y" style={{ borderColor: "#F5EEE0" }}>
          {data.history.length === 0 && <li className="px-4 py-3 text-sm italic opacity-60">Nothing changed yet.</li>}
          {data.history.map((h) => (
            <li key={h.id} className="px-4 py-2.5 text-sm">
              <strong>{h.who}</strong> — {h.what}
              <div className="text-[11px] opacity-60">{when(h.created_at)}</div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function HQOrderCard({ order, locked, onSaved }) {
  const [items, setItems] = useState(order.line_items.map((i) => ({ ...i })));
  const [backups, setBackups] = useState((order.backup_varieties || []).join(", "));
  const [address, setAddress] = useState(typeof order.shipping_address === "string" ? order.shipping_address : "");
  const [notes, setNotes] = useState(order.customer_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await customerApi.patch(`/customer/priority/orders/${order.id}`, {
        line_items: items.map((i) => ({ name: i.name, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 })),
        backup_varieties: backups.split(",").map((b) => b.trim()).filter(Boolean),
        shipping_address: address,
        customer_notes: notes,
      });
      toast.success("Thanks — we've saved your corrections and let Heritage know.");
      await onSaved();
    } catch (e) { toast.error(errMsg(e, "Couldn't save those changes.")); }
    finally { setBusy(false); }
  };

  return (
    <article className="bg-white border p-4 md:p-5 space-y-3" style={{ borderColor: EDGE }} data-testid={`hq-priority-order-${order.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong className="font-serif text-2xl">#{order.order_number}</strong>
        <span className="text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#F2EAF3]">{order.kind}{order.mystery_tier ? ` · ${order.mystery_tier}` : ""}</span>
        <span className="text-xs opacity-60">{when(order.created_at)}</span>
        <span className="ml-auto font-serif text-2xl" data-testid={`hq-priority-order-total-${order.id}`}>NZ${Number(order.total).toFixed(2)}</span>
      </div>

      <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2 text-sm">
        <div>Amount paid: <strong>NZ${Number(order.amount_paid).toFixed(2)}</strong></div>
        <div>Amount owing: <strong className={order.amount_owing > 0 ? "text-[#8E2323]" : ""}>NZ${Number(order.amount_owing).toFixed(2)}</strong></div>
        <div>Payment: <strong>{order.payment_status.replace("_", " ")}</strong>{order.payment_method ? ` · ${order.payment_method}` : ""}</div>
        <div>Shipping: <strong>NZ${Number(order.shipping).toFixed(2)}</strong>{order.discount ? ` · discount NZ$${Number(order.discount).toFixed(2)}` : ""}</div>
        <div>Fulfilment: <strong>{order.stage}</strong>{order.shipping_status ? ` · ${order.shipping_status}` : ""}</div>
      </div>

      {order.customer_visible_notes && (
        <p className="text-sm italic bg-[#FBF6E9] p-3 border-l-4 border-[#C9A268]" data-testid={`hq-priority-note-${order.id}`}>
          From Heritage: {order.customer_visible_notes}
        </p>
      )}

      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold opacity-70 mb-1">Varieties &amp; quantities</div>
        {locked ? (
          <ul className="text-base space-y-0.5">
            {items.map((i, idx) => <li key={idx}>{i.qty}× {i.name}</li>)}
            {items.length === 0 && <li className="italic opacity-60">Nothing listed yet.</li>}
          </ul>
        ) : (
          <div className="space-y-1.5">
            {items.map((i, idx) => (
              <div key={idx} className="flex gap-2">
                <input value={i.name} onChange={(e) => setItems(items.map((x, n) => n === idx ? { ...x, name: e.target.value } : x))}
                  data-testid={`hq-priority-item-${order.id}-${idx}`} className="flex-1 h-11 px-3 border bg-white text-base" style={{ borderColor: EDGE }} />
                <input type="number" min="0" value={i.qty} onChange={(e) => setItems(items.map((x, n) => n === idx ? { ...x, qty: e.target.value } : x))}
                  data-testid={`hq-priority-qty-${order.id}-${idx}`} className="w-20 h-11 px-3 border bg-white text-base text-center" style={{ borderColor: EDGE }} />
                <button type="button" onClick={() => setItems(items.filter((_, n) => n !== idx))} aria-label="Remove"
                  data-testid={`hq-priority-remove-${order.id}-${idx}`} className="p-2 text-[#8E2323]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setItems([...items, { name: "", qty: 1, unit_price: 0 }])} data-testid={`hq-priority-item-add-${order.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: PLUM }}>
              <Plus className="w-4 h-4" /> Add a variety
            </button>
          </div>
        )}
      </div>

      {!locked && (
        <>
          <label className="block text-sm">
            <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold opacity-70 mb-1">My backup varieties</span>
            <input value={backups} onChange={(e) => setBackups(e.target.value)} data-testid={`hq-priority-backups-${order.id}`}
              className="w-full h-11 px-3 border bg-white text-base" style={{ borderColor: EDGE }} />
          </label>
          <label className="block text-sm">
            <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold opacity-70 mb-1">Shipping address</span>
            <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid={`hq-priority-address-${order.id}`}
              className="w-full h-11 px-3 border bg-white text-base" style={{ borderColor: EDGE }} />
          </label>
          <label className="block text-sm">
            <span className="block text-[10px] tracking-[0.2em] uppercase font-semibold opacity-70 mb-1">Notes for Heritage</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid={`hq-priority-notes-${order.id}`}
              className="w-full p-3 border bg-white text-base" style={{ borderColor: EDGE }} />
          </label>
          <div className="flex justify-end">
            <button type="button" onClick={save} disabled={busy} data-testid={`hq-priority-save-${order.id}`}
              className="h-12 px-5 bg-[#6E1E3A] text-white text-xs font-semibold tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save my changes
            </button>
          </div>
        </>
      )}
      {locked && backups && <p className="text-sm">Backup varieties: {backups}</p>}
      {locked && address && <p className="text-sm">Shipping to: {address}</p>}
    </article>
  );
}
